import { useState } from 'react';

import { CoreBox, CoreTooltip, CoreTypography } from '@youscience/core';

import useCommonAppStore from '@store/CommonAppStore';

import ActionButton from '@components/ActionButton';

import CounselorInvitationStatusAcceptedIcon from '@images/counselor-invitation-accepted.svg?react';
import AddIcon from '@images/plus-button.svg?react';
import DeleteIcon from '@images/trash-button.svg?react';

import {
  StyledContentCounselor,
  StyledContentCounselorDescription,
  StyledContentCounselorDescriptionStatus,
  StyledContentCounselorDescriptionText,
} from './AddCounselor.styles';

import CounselorSelection from '../CounselorSelection';

import { PersonaType, Recommender } from '@youscience/commonapp-service-common';

type CounselorWithCommonApp = {
  isFromCommonApp?: boolean;
  recommenderId?: number;
  firstName?: string;
  lastName?: string;
  [key: string]: unknown;
};

type RecommenderWithCommonApp = Recommender & {
  isFromCommonApp?: boolean;
};

export const AddCounselor = () => {
  const { applicant, removeCounselor } = useCommonAppStore((state) => state);

  const [isOpenCounselorSelection, setIsOpenCounselorSelection] = useState(false);

  const handleAddCounselor = () => {
    setIsOpenCounselorSelection(true);
  };

  const handleCloseCounselorSelection = () => {
    setIsOpenCounselorSelection(false);
  };

  const handleRemoveCounselor = async () => {
    await removeCounselor();
  };

  const youScienceCounselor = applicant?.counselor;
  const commonAppCounselor = (applicant?.members?.[0]?.recommenders as RecommenderWithCommonApp[] | undefined)?.find(
    (rec) => rec.personaType === PersonaType.COUNSELOR && rec.isFromCommonApp,
  );
  const counselor = (youScienceCounselor ?? commonAppCounselor) as CounselorWithCommonApp | undefined;
  const isCounselorFormSubmitted = applicant?.members?.some((member) =>
    member?.recommenders.some(
      (recommender) =>
        recommender.personaType === PersonaType.COUNSELOR &&
        recommender.recommenderId === counselor?.recommenderId &&
        recommender.forms.some((form) => !!form?.submittedDate),
    ),
  );

  const isFromCommonApp = counselor?.isFromCommonApp ?? false;
  const isRemoveDisabled = isCounselorFormSubmitted || isFromCommonApp;

  return (
    <>
      <StyledContentCounselor>
        <StyledContentCounselorDescription>
          {counselor ? (
            <StyledContentCounselorDescriptionStatus variant="circular">
              <CoreTooltip title="In progress">
                <CoreBox>
                  <CounselorInvitationStatusAcceptedIcon />
                </CoreBox>
              </CoreTooltip>
            </StyledContentCounselorDescriptionStatus>
          ) : null}
          <StyledContentCounselorDescriptionText>
            <CoreTypography variant="overline" component="h6">
              My Counselor
            </CoreTypography>
            <CoreTypography variant="body2">
              {counselor ? `${counselor?.firstName} ${counselor?.lastName}` : 'Not assigned'}
            </CoreTypography>
          </StyledContentCounselorDescriptionText>
        </StyledContentCounselorDescription>
        <CoreTooltip title={counselor && isFromCommonApp ? "This counselor was added on Common App's website." : ''}>
          <CoreBox>
            <ActionButton
              type="button"
              data-testid="actionBtn"
              variant="outlined"
              onClick={counselor ? handleRemoveCounselor : handleAddCounselor}
              busyText={counselor ? 'Removing' : 'Add'}
              startIcon={counselor ? <DeleteIcon /> : <AddIcon />}
              disabled={counselor ? isRemoveDisabled : false}
            >
              {counselor ? 'Remove' : 'Add'}
            </ActionButton>
          </CoreBox>
        </CoreTooltip>
      </StyledContentCounselor>
      {isOpenCounselorSelection ? (
        <CounselorSelection
          isOpenCounselorSelection={isOpenCounselorSelection}
          onCloseCounselorSelection={handleCloseCounselorSelection}
        />
      ) : null}
    </>
  );
};
